import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { gqlClient } from '../clients';
import { graphql } from '../gql';
import { Scheduling_JobStatus } from '../gql/graphql';

interface BackgroundTasksContextValue {
  trackTask: (executionId: string) => void;
  trackedIds: Set<string>;
  isPopupOpen: boolean;
  openPopup: () => void;
  closePopup: () => void;
}

const BackgroundTasksContext =
  createContext<BackgroundTasksContextValue | null>(null);

const activeTasksQuery = graphql(/* GraphQL */ `
  query backgroundTasksActiveTasks($filter: Tasks_MyTasksFilter) {
    tasks_tasks(filter: $filter) {
      executionId
    }
  }
`);

const ACTIVE_STATUSES = [
  Scheduling_JobStatus.Queued,
  Scheduling_JobStatus.Pending,
  Scheduling_JobStatus.Running,
];

interface BackgroundTasksProviderProps {
  children: React.ReactNode;
}

export function BackgroundTasksProvider({
  children,
}: BackgroundTasksProviderProps) {
  const [trackedIds, setTrackedIds] = useState<Set<string>>(new Set());
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openPopup = useCallback(() => setIsPopupOpen(true), []);
  const closePopup = useCallback(() => setIsPopupOpen(false), []);

  const trackTask = useCallback(async (executionId: string) => {
    setTrackedIds((prev) => {
      const next = new Set(prev);
      next.add(executionId);
      return next;
    });
    setIsPopupOpen(true);

    try {
      const result = await gqlClient.request(activeTasksQuery, {
        filter: { statuses: ACTIVE_STATUSES },
      });
      const activeIds =
        result?.tasks_tasks?.map((task) => task.executionId) ?? [];
      if (activeIds.length > 0) {
        setTrackedIds((prev) => {
          const next = new Set(prev);
          for (const activeId of activeIds) {
            next.add(activeId);
          }
          return next;
        });
      }
    } catch {
      // Non-critical: if discovering active tasks fails, we still track the explicit ID
    }
  }, []);

  const value = useMemo<BackgroundTasksContextValue>(
    () => ({
      trackTask,
      trackedIds,
      isPopupOpen,
      openPopup,
      closePopup,
    }),
    [trackTask, trackedIds, isPopupOpen, openPopup, closePopup],
  );

  return (
    <BackgroundTasksContext.Provider value={value}>
      {children}
    </BackgroundTasksContext.Provider>
  );
}

export function useBackgroundTasks(): BackgroundTasksContextValue {
  const context = useContext(BackgroundTasksContext);
  if (!context) {
    throw new Error(
      'useBackgroundTasks must be used within a BackgroundTasksProvider',
    );
  }
  return context;
}

export function useTrackTask() {
  const { trackTask } = useBackgroundTasks();
  return trackTask;
}
